"use client";

import { useSession } from 'next-auth/react';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';

import { useTranslation } from "../hooks/useTranslation";
import {
  APP_NAVIGATED_EVENT,
  DEFAULT_TAB,
  TAB_STORAGE_KEY,
  dispatchSpaNavigate,
  getTopLevelTab,
} from "../lib/spa-navigation";

/**
 * The game-level navigation row. The markup and class names come from the
 * template integration guide, so the styling arrives with the hosted
 * stylesheet — note it hides the row entirely when only one item renders.
 */
export default function GameNav() {
  const { data: session, status } = useSession({ required: false });
  const { t } = useTranslation();
  const pathname = usePathname();

  const teamId = (session?.user as any)?.teamId as string | undefined;

  // Pages that serve the SPA shell from a non-root path (/rules, /prizes) boot
  // the in-memory router at that tab, so the URL is the right initial guess.
  // Once hydrated, the router is the source of truth via APP_NAVIGATED_EVENT.
  const [activeHref, setActiveHref] = useState<string>(
    () => getTopLevelTab(pathname ?? '/') ?? DEFAULT_TAB,
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (!getTopLevelTab(pathname ?? '/')) {
      try {
        const stored = window.sessionStorage.getItem(TAB_STORAGE_KEY);
        if (stored) setActiveHref(stored);
      } catch {
        // sessionStorage unavailable (e.g. private mode)
      }
    }

    const onNavigated = (event: Event) => {
      const detail = (event as CustomEvent<string>).detail;
      if (typeof detail !== 'string' || !detail) return;
      setActiveHref(getTopLevelTab(detail) ?? detail);
    };
    window.addEventListener(APP_NAVIGATED_EVENT, onNavigated);
    return () => window.removeEventListener(APP_NAVIGATED_EVENT, onNavigated);
  }, [pathname]);

  // Anything the in-memory router owns navigates through the custom event so the
  // browser URL stays locked at "/" inside the native webview. Web-only links
  // such as /fantasy/edit/:teamId fall through as real anchors.
  const handleClick = useCallback((href: string, event: React.MouseEvent<HTMLAnchorElement>) => {
    if (!getTopLevelTab(href)) return;
    event.preventDefault();
    setActiveHref(href);
    dispatchSpaNavigate(href);
  }, []);

  const navigation: { name: string; href: string }[] = [
    { name: t('menu.predictions'), href: '/fantasy/predictions' },
  ];

  if (status === 'authenticated' && session?.user && teamId) {
    navigation.push({ name: t('menu.team'), href: `/fantasy/edit/${teamId}` });
  }

  navigation.push({ name: t('menu.prizes'), href: '/prizes' });
  navigation.push({ name: t('menu.rules'), href: '/rules' });

  return (
    <nav className="navigation" id="game-nav" aria-label="Game navigation">
      <div className="navigationContainer">
        {navigation.map((item) => {
          const current = item.href === activeHref;
          return (
            <a
              key={item.href}
              className={current ? 'navigationItem active' : 'navigationItem'}
              href={item.href}
              aria-current={current ? 'page' : undefined}
              onClick={(event) => handleClick(item.href, event)}
            >
              {item.name}
            </a>
          );
        })}
      </div>
    </nav>
  );
}
